// Admin Costs page — at-a-glance infrastructure + Agent Console spend.
//
// Two non-overlapping pipelines (no double-count): Agent Console + Co-Pilots
// (agent_console_message.cost_usd, broken out by door) and AI/API background
// services (api_usage_logs, by provider). The combined headline + 30-day trend
// come from /admin/costs/overview; the per-window door/provider breakdowns come
// from /admin/costs/agent-console and the existing /admin/costs/summary.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { costTrackingService } from '../services/costTracking';
import type {
  AgentConsoleCostSummary,
  CostOverview,
  CostSummary,
} from '../services/costTracking';
import { formatMoney } from '../utils/formatting';
import styles from './CostsPage.module.css';

type CostWindow = 'today' | 'mtd' | 'd30' | 'all';

const WINDOWS: { key: CostWindow; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'mtd', label: 'Month to date' },
  { key: 'd30', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
];

const num = (s: string | undefined): number => (s ? parseFloat(s) || 0 : 0);

function rangeFor(w: CostWindow): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date();
  if (w === 'mtd') start.setDate(1);
  else if (w === 'd30') start.setDate(end.getDate() - 29);
  else if (w === 'all') start.setFullYear(2000, 0, 1);
  // 'today' keeps start on the current day
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

function shareSentence(w: CostWindow, ac: number, combined: number): string {
  if (combined <= 0) return 'No spend recorded in this window yet.';
  const pct = Math.round((ac / combined) * 100);
  switch (w) {
    case 'today':
      return `Agent Console is ${pct}% of today's spend so far.`;
    case 'mtd':
      return `Agent Console is ${pct}% of this month's spend.`;
    case 'all':
      return `Agent Console is ${pct}% of all-time spend.`;
    default:
      return `Agent Console is ${pct}% of the last 30 days — the largest single line.`;
  }
}

export function CostsPage() {
  const { user, hasScope } = useAuth();
  // Admins and executive-portal readers (exec:portal — the backend read guard
  // on /admin/costs/* admits both).
  const isAdmin = user?.role === 'ADMIN' || hasScope('exec:portal');

  const [overview, setOverview] = useState<CostOverview | null>(null);
  const [acSummary, setAcSummary] = useState<AgentConsoleCostSummary | null>(null);
  const [apiSummary, setApiSummary] = useState<CostSummary | null>(null);
  const [window, setWindow] = useState<CostWindow>('d30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Overview (all windows + 30-day trend) is window-independent — load once.
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    costTrackingService
      .getOverview(true)
      .then((o) => alive && setOverview(o))
      .catch(() => alive && setError('Failed to load cost overview'));
    return () => {
      alive = false;
    };
  }, [isAdmin]);

  // Per-window breakdowns (Agent Console by door + API by provider). setState
  // happens only in the async resolution (never synchronously in the effect),
  // so switching windows swaps the data in place without a loading flash.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const { start, end } = rangeFor(window);
    Promise.allSettled([
      costTrackingService.getAgentConsole(start, end),
      costTrackingService.getSummary(start, end),
    ]).then(([ac, api]) => {
      if (cancelled) return;
      if (ac.status === 'fulfilled') setAcSummary(ac.value);
      if (api.status === 'fulfilled') setApiSummary(api.value);
      if (ac.status === 'rejected' && api.status === 'rejected') {
        setError('Failed to load cost breakdowns');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, window]);

  const wt = overview?.windows[window];
  const acTotal = num(wt?.agent_console_usd);
  const apiTotal = num(wt?.api_usd);
  const combined = num(wt?.combined_usd);
  const acPct = combined > 0 ? (acTotal / combined) * 100 : 0;
  const apiPct = combined > 0 ? 100 - acPct : 0;

  const trendMax = useMemo(() => {
    if (!overview?.daily?.length) return 0.01;
    return Math.max(...overview.daily.map((d) => num(d.combined_usd)), 0.01);
  }, [overview]);

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Costs</h1>
        <div className={styles.notice}>This dashboard is available to admins and executive readers only.</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.topline}>
        <div>
          <h1 className={styles.title}>Costs</h1>
          <p className={styles.subtitle}>
            Infrastructure &amp; AI spend at a glance — Agent Console broken out as its own line.
          </p>
          {overview && (
            <div className={styles.liveband}>
              <span className={styles.dotg} /> Live · updated{' '}
              {new Date(overview.generated_at).toLocaleString()}
            </div>
          )}
        </div>
        <div className={styles.seg} role="tablist" aria-label="Time window">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              className={window === w.key ? styles.segOn : ''}
              aria-pressed={window === w.key}
              onClick={() => setWindow(w.key)}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.cards}>
        {/* headline */}
        <div className={styles.card + ' ' + styles.headline}>
          <div>
            <div className={styles.cardHead}>
              Total spend · {WINDOWS.find((w) => w.key === window)?.label.toLowerCase()}
            </div>
            <div className={styles.bigtotal}>
              <span className={styles.cur}>$</span>
              {combined.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className={styles.htsub}>{shareSentence(window, acTotal, combined)}</div>
          </div>
          <div>
            <div className={styles.splitbar}>
              <i style={{ width: `${acPct}%`, background: 'var(--ac)' }} title={`Agent Console ${formatMoney(acTotal)}`} />
              <i style={{ width: `${apiPct}%`, background: 'var(--api)' }} title={`AI / API ${formatMoney(apiTotal)}`} />
            </div>
            <div className={styles.legend}>
              <div className={styles.li}>
                <span className={styles.sw} style={{ background: 'var(--ac)' }} /> Agent Console{' '}
                <b>{formatMoney(acTotal)}</b>
                <span className={styles.pct}>· {acPct.toFixed(0)}%</span>
              </div>
              <div className={styles.li}>
                <span className={styles.sw} style={{ background: 'var(--api)' }} /> AI / API services{' '}
                <b>{formatMoney(apiTotal)}</b>
                <span className={styles.pct}>· {apiPct.toFixed(0)}%</span>
              </div>
            </div>
            {overview && (
              <div className={styles.ctx}>
                <CtxStat k="Today" v={overview.windows.today?.combined_usd} />
                <CtxStat k="Month-to-date" v={overview.windows.mtd?.combined_usd} />
                <CtxStat k="All-time" v={overview.windows.all?.combined_usd} />
              </div>
            )}
          </div>
        </div>

        {/* Agent Console by door */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            Agent Console &amp; Merlin{' '}
            <span className={styles.win30}>· {WINDOWS.find((w) => w.key === window)?.label.toLowerCase()}</span>
          </div>
          {loading && !acSummary ? (
            <div className={styles.loading}>Loading…</div>
          ) : (
            <>
              <DoorRows summary={acSummary} />
              <div className={styles.cardTotal}>
                <span className={styles.muted}>
                  Σ Agent Console · {acSummary?.turn_count ?? 0} turns
                </span>
                <span className={styles.v}>{formatMoney(acSummary?.total_usd)}</span>
              </div>
            </>
          )}
        </div>

        {/* AI / API by provider */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            AI / API services{' '}
            <span className={styles.win30}>· {WINDOWS.find((w) => w.key === window)?.label.toLowerCase()}</span>
          </div>
          {loading && !apiSummary ? (
            <div className={styles.loading}>Loading…</div>
          ) : (
            <>
              <ProviderRows summary={apiSummary} />
              <div className={styles.cardTotal}>
                <span className={styles.muted}>
                  Σ AI / API · {(apiSummary?.totals.call_count ?? 0).toLocaleString()} calls
                </span>
                <span className={styles.v}>{formatMoney(apiSummary?.totals.cost_usd)}</span>
              </div>
            </>
          )}
        </div>

        {/* daily trend */}
        <div className={styles.secthead}>
          <h2>Daily spend — last 30 days</h2>
          <span className={styles.ln} />
          <span className={styles.muted}>stacked: Agent Console + AI/API</span>
        </div>
        <div className={styles.card + ' ' + styles.trend}>
          {overview?.daily?.length ? (
            <>
              <div className={styles.chart}>
                {overview.daily.map((d) => {
                  const total = num(d.combined_usd);
                  const h = (total / trendMax) * 158;
                  const ac = num(d.agent_console_usd);
                  const acH = total > 0 ? (ac / total) * h : 0;
                  const apiH = h - acH;
                  return (
                    <div
                      key={d.date}
                      className={styles.col}
                      title={`${d.date}: Agent Console ${formatMoney(d.agent_console_usd)} · API ${formatMoney(d.api_usd)}`}
                    >
                      <span style={{ height: `${apiH}px`, background: 'var(--api)' }} />
                      <span style={{ height: `${acH}px`, background: 'var(--ac)', borderRadius: '2px 2px 0 0' }} />
                    </div>
                  );
                })}
              </div>
              <div className={styles.axis}>
                <span>{overview.daily[0]?.date}</span>
                <span>{overview.daily[overview.daily.length - 1]?.date}</span>
              </div>
            </>
          ) : (
            <div className={styles.loading}>No trend data.</div>
          )}
        </div>

        {/* optional GCP hosting */}
        <div className={styles.card + ' ' + styles.optional}>
          <span className={styles.ghost}>$ —</span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className={styles.cardHead}>Cloud hosting (GCP) — not included</div>
            <p className={styles.muted}>
              These figures are AI/API spend only. GCP compute, Cloud SQL and storage bills are not
              in our database — they can be folded in later via the GCP billing export.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CtxStat({ k, v }: { k: string; v: string | undefined }) {
  return (
    <div className={styles.ctxItem}>
      <div className={styles.ctxK}>{k}</div>
      <div className={styles.ctxV}>{formatMoney(v)}</div>
    </div>
  );
}

function DoorRows({ summary }: { summary: AgentConsoleCostSummary | null }) {
  const doors = summary?.by_door ?? [];
  if (doors.length === 0) return <div className={styles.muted}>No Agent Console spend in this window.</div>;
  const max = Math.max(...doors.map((d) => num(d.cost_usd)), 0.0001);
  return (
    <>
      {doors.map((d) => (
        <div key={d.door_id} className={styles.row}>
          <span className={styles.rowName} title={d.label}>{d.label}</span>
          <span className={styles.bar}>
            <i style={{ width: `${(num(d.cost_usd) / max) * 100}%`, background: 'var(--ac)' }} />
          </span>
          <span className={styles.amt}>{formatMoney(d.cost_usd)}</span>
          <span className={styles.cnt}>{d.turn_count.toLocaleString()} turns</span>
        </div>
      ))}
    </>
  );
}

function ProviderRows({ summary }: { summary: CostSummary | null }) {
  const providers = Object.entries(summary?.by_provider ?? {}).sort(
    ([, a], [, b]) => parseFloat(b.cost_usd) - parseFloat(a.cost_usd),
  );
  if (providers.length === 0) return <div className={styles.muted}>No AI/API spend in this window.</div>;
  const max = Math.max(...providers.map(([, p]) => num(p.cost_usd)), 0.0001);
  return (
    <>
      {providers.map(([name, p]) => (
        <div key={name} className={styles.row}>
          <span className={styles.rowName} title={name}>{name}</span>
          <span className={styles.bar}>
            <i style={{ width: `${(num(p.cost_usd) / max) * 100}%`, background: 'var(--api)' }} />
          </span>
          <span className={styles.amt}>{formatMoney(p.cost_usd)}</span>
          <span className={styles.cnt}>{p.count.toLocaleString()} calls</span>
        </div>
      ))}
    </>
  );
}
